import Enhancer from '../enhancer.js';
import EnhancerApp from '../enhancer-app.js';
import * as dateUtil from '../utils/date';

export default class EventCalendarEnhancer extends Enhancer {
    url = '';
    target = undefined;
    selectedDate = null;
    months = [
        'Januari',
        'Februari',
        'Mars',
        'April',
        'Maj',
        'Juni',
        'Juli',
        'Augusti',
        'September',
        'Oktober',
        'November',
        'December',
    ];
    weekDays = [
        'Måndag',
        'Tisdag',
        'Onsdag',
        'Torsdag',
        'Fredag',
        'Lördag',
        'Söndag',
    ];

    init() {
        this.url = this.element.dataset.getMonthUrl.startsWith('http')
            ? this.element.dataset.getMonthUrl
            : location.origin + '/' + this.element.dataset.getMonthUrl;

        this.target = document.querySelector(
            '#' + this.element.dataset.getMonthTarget
        );

        // If url is shared take month query from url else selectedDate
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('month')) {
            this.selectedDate = dateUtil.getDateWithoutTime(new Date());
            this.selectedDate.setDate(1);
            this.selectedDate.setMonth(Number(urlParams.get('month')));
        }

        if (urlParams.get('year')) {
            this.selectedDate.setFullYear(Number(urlParams.get('year')));
        }

        if (!this.selectedDate) {
            this.selectedDate = dateUtil.getDateWithoutTime(new Date());
        }

        // Bind clicks
        this.previousButton.addEventListener(
            'click',
            this.getPreviousMonth.bind(this)
        );
        this.nextButton.addEventListener('click', this.getNextMonth.bind(this));

        // Create content inside date picker
        this.createMonth();

        document.body.addEventListener('click', (e) => {

            if (e.target.id === '#button-next-available-month') {
                const nextAvailableMonth = new Date(e.target.dataset.nextAvailableMonth);

                this.goToMonth(nextAvailableMonth);
            }

        });
        
    }

    setStateForButtons(month) {
        if (this.startDate - this.selectedDate >= 0) {
            this.previousButton.disabled = true;
        }
        else {
            this.previousButton.disabled = false;
        }

        if (this.endDate - this.selectedDate <= 0) {
            this.nextButton.disabled = true;
        }
        else {
            this.nextButton.disabled = false;
        }
    }

    clearCalendar() {
        this.calendarBody.innerHTML = '';
    }

    getPreviousMonth() {

        this.selectedDate = dateUtil.addMonths(this.selectedDate, -1);
        this.selectedDate = dateUtil.getDateWithoutTime(this.selectedDate);
        this.goToMonth(this.selectedDate);

    }

    getNextMonth() {

        this.selectedDate = dateUtil.addMonths(this.selectedDate, 1);
        this.selectedDate = dateUtil.getDateWithoutTime(this.selectedDate);
        this.goToMonth(this.selectedDate);

    }

    goToMonth(date) {
        this.selectedDate = dateUtil.getFirstDayOfMonth(date);
        this.selectedDate = dateUtil.getDateWithoutTime(this.selectedDate);
        this.fetchCalendarItems(this.selectedDate);
        this.setStateForButtons();
    }

    fetchCalendarItems(date) {
        const url = new URL(this.url, document.baseURI);
        const params = new URLSearchParams(url.search);

        params.set('month', date.getMonth());
        params.set('year', date.getFullYear());
        url.search = params;

        fetch(url.toString()).then((response) => {
            window.history.pushState(
                '{Month: ' + date.getMonth() + ', Year: ' + date.getFullYear() + '}',
                document.title,
                '?' + params.toString()
            );

            response.text().then((text) => {
                // Get dates from html result and populate this.availableDays
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');

                const dates = doc.querySelectorAll('.calendar-day');
                const dateArray = Array.from(dates);

                const availableDays = dateArray.map((d) => {
                    return parseInt(d.innerHTML.trim());
                });

                this.availableDays = availableDays;

                // Clear from previous month
                this.target.innerHTML = '';

                // Create and insert screen reader text above events
                const srText = document.createElement('p');

                srText.className = 'visually-hidden';
                srText.innerHTML = `Visar händelser för ${this.months[date.getMonth()]}`;

                this.target.insertBefore(srText, this.target.childNodes[0]);

                // Add new events to list
                this.target.innerHTML = this.target.innerHTML + text;

                // Update datepicker with new month
                this.createMonth(date.getMonth());

                // If chosen month doesn't have any events check for button and add
                // functionality to go to next month that has events
                if (response.headers.get('no-available-dates') === 'true') {
                    const button = document.querySelector(
                        '#button-next-available-month'
                    );

                    if (button !== null && button !== undefined && button.dataset.nextAvailableMonth != 0) {
                        const nextAvailableMonth = new Date(button.dataset.nextAvailableMonth);

                        button.addEventListener('click', () => {
                            this.goToMonth(nextAvailableMonth);
                        });
                    }
                }
            });
        });
    }

    // See solution more explained here: https://stackoverflow.com/a/6117889
    getWeekNumber(date) {
        date = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
        );

        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));

        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);

        return weekNumber;
    }

    createDays(date) {
        const year = date.getFullYear();
        const month = date.getMonth(); // From 0-11

        // Get all days for the requested month and save i array
        const numberOfDaysInMonth = new Date(
            year,
            month + 1, // Next month.
            0 // Last date this month.
        ).getDate();

        const daysInMonth = [...Array(numberOfDaysInMonth)].map(
            (_value, i) => new Date(year, month, i + 1)
        );

        // Get get last days from previous month to fill up the week visually
        const firstDayOfMonth = new Date(year, month, 1).getDay();

        const numberOfDaysFromPrevMonth =
            firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // getDay() returns a range from 0-6 where sunday is 0 but we want to start the week with monday

        const daysFromPrevMonth = [...Array(numberOfDaysFromPrevMonth)].map(
            (_value, i) =>
                new Date(year, month, -(numberOfDaysFromPrevMonth - 1) + i)
        );

        // Get the first days from next month to fill upp the week visually
        const lastDayOfMonth = new Date(
            year,
            month,
            numberOfDaysInMonth
        ).getDay();

        const numberOfDaysFromNextMonth =
            6 - (lastDayOfMonth === 0 ? 6 : lastDayOfMonth - 1); // getDay() returns a range from 0-6 where sunday is 0 but we want to start the week with monday

        const daysFromNextMonth = [...Array(numberOfDaysFromNextMonth)].map(
            (_value, i) => new Date(year, month + 1, i + 1)
        );

        // Concatenate all of the arrays to create a array of the complete month
        const days = [
            ...daysFromPrevMonth,
            ...daysInMonth,
            ...daysFromNextMonth,
        ];

        days.forEach((date, i) => {
            // Every 7nth iteration create a tile with the week number
            if (i % 7 === 0) {
                const weekContainer = document.createElement('div');
                const weekNumber = this.getWeekNumber(date);

                weekContainer.className =
                    'date-picker-week border-end border-bottom py-1 px-3 var-fw-650 text-uppercase d-flex align-items-start justify-content-center';
                weekContainer.innerHTML = 'v.' + weekNumber;

                // Add the <abbr> attribute to make sense of the text (ex V.44)
                weekContainer.setAttribute('abbr', `Vecka ${weekNumber}`);

                this.calendarBody.appendChild(weekContainer);
            }

            const buttonContainer = document.createElement('div');
            const button = document.createElement('button');
            // This seems unnecessary but it's there to style the underline correctly
            const buttonText = document.createElement('span');

            // Create a button with date and aria-label (Ex Onsdag, 14 December)
            buttonText.innerHTML = date.getDate();
            buttonText.setAttribute(
                'aria-label',
                `${this.weekDays[date.getDay()]}, ${date.getDate()} ${
                    this.months[date.getMonth()]
                }`
            );
            button.appendChild(buttonText);

            buttonContainer.className =
                'date-picker-date border-end border-bottom';
            button.className =
                'btn date-picker-btn py-1 px-3 d-flex justify-content-center align-items-start w-100 h-100 fs-sm opacity-100';
            button.disabled = true;

            buttonContainer.appendChild(button);
            this.calendarBody.appendChild(buttonContainer);


            const currentDate = new Date();

            const isCurrentMonth = date.getMonth() === month;

            // If a date is contained within the requested month (ie not one of the first/last dates from previous or next month)
            // and is included in the availableDays property add a active styling and enable the button
            if (isCurrentMonth && this.availableDays.includes(date.getDate())) {
                button.disabled = false;
                buttonText.className =
                    'date-picker-button-text border-bottom border-5 border-dark';
                button.setAttribute('data-available', true);
            }

            if (!isCurrentMonth) {
                button.classList.add('opacity-50');
            }
        });

        this.createScrollButtons();
    }

    createScrollButtons() {
        // Find all buttons that is has a available date connected to it and
        const buttons = document.querySelectorAll(
            '.date-picker-btn[data-available]'
        );

        // Find all calendar items in list
        const events = Array.from(document.querySelectorAll('.calendar-item'));

        // All buttons that has at least one matching date with calendar item
        // gets an eventListener so user can click and scroll to first matching calendar item
        buttons.forEach((button) => {
            const element = events.filter((el) => {
                return (
                    el.dataset.day ===
                    button.querySelector('.date-picker-button-text').innerHTML
                );
            });

            button.addEventListener('click', () => {
                element[0].scrollIntoView();
                element[0].focus();
            });
        });
    }

    createMonth() {
        this.clearCalendar();
        this.createDays(this.selectedDate);
        this.calendarHead.innerHTML = `${this.months[this.selectedDate.getMonth()]} ${this.selectedDate.getFullYear()}`;
        this.setStateForButtons(this.selectedDate.getMonth());
    }

    get availableDays() {
        const dates = this.element.dataset.availableDays.split(',');
        const datesArray = Array.from(dates, (date) => parseInt(date));

        return datesArray;
    }

    set availableDays(value) {
        this.element.dataset.availableDays = value;
    }

    get calendarBody() {
        return document.querySelector(this.element.dataset.datePickerBody);
    }

    get calendarHead() {
        return document.querySelector(this.element.dataset.datePickerHeader);
    }

    get nextButton() {
        return document.querySelector(this.element.dataset.arrowForward);
    }

    get previousButton() {
        return document.querySelector(this.element.dataset.arrowBackwards);
    }

    get startDate() {
        return this.element.dataset.startDate ? dateUtil.getDateWithoutTime(new Date(this.element.dataset.startDate)) : null;
    }

    get endDate() {
        return this.element.dataset.endDate ? dateUtil.getDateWithoutTime(new Date(this.element.dataset.endDate)) : null;
    }
}

// Enhancers must be registered with a name that is used in the data-enhancer attribute
EnhancerApp.register('EventCalendar', EventCalendarEnhancer);
